<template>
	<Container class="cookie-notice">
		<h2 id="cookies-policy">Cookies Policy</h2>
		<p>
			To make the websites of the Codecannon (Andrej Fidel s.p.) work properly, we sometimes place
			small data files called ‘cookies’ on your device.
		</p>
		<h3 id="what-are-cookies-">What are cookies?</h3>
		<p>
			A cookie is a small text file that a website stores on your computer or mobile device when you
			visit the site. It enables the website to remember your actions and preferences (such as
			login, language, font size and other display preferences) over a period of time, so you don’t
			have to keep re-entering them whenever you come back to the site or browse from one page to
			another.
		</p>
		<ul>
			<li>
				<strong>First-party cookies</strong> are cookies set by the website you’re visiting. Only
				that website can read them. In addition, a website might potentially use external services,
				which also set their own cookies, known as <strong>third-party cookies</strong>.
			</li>
			<li>
				<strong>Persistent cookies</strong> are cookies that are saved on your computer and are not
				deleted automatically when you quit your browser, unlike a session cookie, which is deleted
				when you quit your browser.
			</li>
		</ul>
		<h3 id="how-do-we-use-cookies-">How do we use cookies?</h3>
		<p>
			The Codecannon (Andrej Fidel s.p.) websites exclusively use ‘first-party cookies’. These are
			cookies set and controlled by Codecannon (Andrej Fidel s.p.), not by any external
			organisations.
		</p>
		<p>
			You can delete or block these cookies, but if you do that, some features of this site may not
			work as intended. These cookies are used to remember:
		</p>
		<ul>
			<li>your authentication tokens and session data;</li>
		</ul>
		<h4 id="operational-cookies">Operational cookies</h4>
		<p>
			There are some cookies that we have to include in order for certain web pages to function. For
			this reason, they do not require your consent. In particular:
		</p>
		<ul>
			<li><strong>authentication cookies</strong>;</li>
		</ul>
		<h5 id="authentication-cookies">Authentication cookies</h5>
		<p>
			These are stored when you log in to the Codecannon (Andrej Fidel s.p.) sites, using our
			authentication service. When you do this, you accept the associated
			<a href="https://codecannon.dev/privacy-policy">privacy policy<em></em></a>.
		</p>
		<table>
			<thead>
				<tr>
					<th>Name</th>
					<th>Service</th>
					<th>Purpose</th>
					<th>Cookie type and duration</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>codecannon_session</td>
					<td>Identity Management Service</td>
					<td>Identifies the current Service session</td>
					<td>First-party persistent cookie, deleted when your session expires</td>
				</tr>
				<tr>
					<td>remember_web_HASH</td>
					<td>Identity Management Service</td>
					<td>
						Keeps you signed in after your session expires. Set when you sign in with &quot;Remember
						me&quot; checked or with Google or GitHub.
					</td>
					<td>
						Up to 400 days. Cleared when you log out or sign in without &quot;Remember me&quot;.
					</td>
				</tr>
				<tr>
					<td>XSRF-TOKEN</td>
					<td>Identity Management Service</td>
					<td>Protects user from Cross-Site Request Forgery attacks</td>
					<td>First-party persistent cookie, deleted when XSRF token expires</td>
				</tr>
			</tbody>
		</table>
		<h3 id="how-can-you-manage-cookies-">How can you manage cookies?</h3>
		<p>
			You can manage/delete cookies as you wish — for details, see
			<a href="https://aboutcookies.org/">aboutcookies.org<em></em></a>
		</p>
		<h4 id="removing-cookies-from-your-device">Removing cookies from your device</h4>
		<p>
			You can delete all cookies that are already on your device by clearing the browsing history of
			your browser. This will remove all cookies from all websites you have visited.
		</p>
		<p>Be aware though that you may also lose some saved information (e.g. saved login details).</p>
		<h4 id="managing-site-specific-cookies">Managing site-specific cookies</h4>
		<p>
			For more detailed control over site-specific cookies, check the privacy and cookie settings in
			your preferred browser.
		</p>
		<h4 id="blocking-cookies">Blocking cookies</h4>
		<p>
			You can set most modern browsers to prevent any cookies being placed on your device, but you
			may then have to manually adjust some preferences every time you visit a site/page. And some
			services and functionalities may not work properly at all (e.g. logging in to your profile).
		</p>
	</Container>
</template>

<script setup lang="ts">
import Container from '@/components/Container.vue'
</script>

<style lang="scss" scoped>
.cookie-notice {
	max-width: 800px;
	display: flex;
	flex-direction: column;
	gap: 25px;

	h1 {
		font-size: 2rem;
		margin-bottom: 0.5rem;
		border-bottom: 2px solid #eee;
		padding-bottom: 0.5rem;
	}
	h2 {
		font-size: 1.5rem;
		margin-top: 1.5rem;
		margin-bottom: 0.5rem;
	}
	h3 {
		font-size: 1.25rem;
		margin-top: 1rem;
		margin-bottom: 0.5rem;
	}
	p {
		margin-bottom: 1rem;
	}
	ol,
	ul {
		margin: 0 0 1rem 1.5rem;
	}
	li {
		margin-bottom: 0.5rem;
	}
}
</style>
